import cn from "classnames";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useSearchParams } from "next/navigation";
import { useState, useEffect } from "react";

import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";

import { BaseButton } from "@/src/component/base/BaseButton";
import { ImgBgResponsive } from "@/src/component/base/ImgBgResponsive";
import { CarouselImage, CarouselImageType } from "@/src/component/partial/CarouselImage";
import { VideoPlayer } from "@/src/component/partial/VideoPlayer";
import { BrokerProfileLogic } from "@/src/logic";
import CMSLogic from "@/src/logic/CMSLogic";
import { StyleHooks, ProviderHooks, BrowserHooks } from "@/src/hook";
import { ComponentType, StrapiComponentType } from "@/src/type";
import { LoadFromCDN, StringHelper } from "@/src/util";

import { SectionModule, ButtonLinkModule } from "@/src/component/partial/__module__";
import UnitDetailsFacts from "./UnitDetailsFacts";

import theme from "@/styles/scss/theme.module.scss";
import styles from "./UnitDetails.module.scss";

interface UnitDetailsProps extends ComponentType.DynamicPageSectionComponentProps<StrapiComponentType.Section.UnitDetails> {}

const UNIT_TYPE_URL_PARAM_KEY = "unit-type";

function getInitialUnitType(searchParams?: Record<string, string | string[] | undefined>, defaultUnitTypeSlug?: string) {
  const defaultUnitType = defaultUnitTypeSlug ?? "2-bed";
  return StringHelper.checkIsStringOrIsNull(searchParams?.[UNIT_TYPE_URL_PARAM_KEY], defaultUnitType);
}

function UnitDetails({
  section,
  slug,
  snapToGrid,
  searchParams,
  contactUsLink,
  classes,
  quality,
  mobileHint,
  deviceWidthHint,
  lazy,
}: UnitDetailsProps) {
  const router = useRouter();
  const searchParamsHook = useSearchParams();
  const cms = ProviderHooks.useCMS();
  const toast = ProviderHooks.useToast();
  const unitDetails: StrapiComponentType.Section.UnitDetails = section.type?.[0];
  const [navigationOption, setNavigationOption] = useState<string>(getInitialUnitType(searchParams, unitDetails?.defaultUnitTypeSlug));
  const [openImageDialog, setOpenImageDialog] = useState<boolean>(false);
  const [drawerOpen, setDrawerOpen] = useState<StrapiComponentType.Partial.UnitTypeDetail | undefined>(undefined);
  const breakpointMdKey = `@media (max-width: ${theme.breakpointMD})`;
  const isMobile = StyleHooks.useBreakPointDown("md");
  const imageSize = mobileHint ? deviceWidthHint : 1920;
  const hasProfile = BrokerProfileLogic.checkQueryObjectHasProfile(searchParams);
  const externalLink = BrowserHooks.useLinkWithTrackingParams(contactUsLink ?? "", true);

  /**
   * disable backdrop, could switch to MUI dialog but this was easier for styling atm
   */
  useEffect(() => {
    if (openImageDialog) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.removeAttribute("style");
    }
  }, [openImageDialog]);

  /**
   * when the user clicks a tab button switch what is render and update the url
   */
  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setNavigationOption(newValue);
    const currentParams = new URLSearchParams(searchParamsHook?.toString());
    currentParams.set(UNIT_TYPE_URL_PARAM_KEY, newValue);
    router.push(`${window?.location?.pathname}?${currentParams.toString()}`, undefined, {
      shallow: true,
    });
  };

  /**
   * close the image gallery
   */
  const handleCloseImageDialog = () => {
    setOpenImageDialog(false);
  };

  /**
   * open the image gallery
   */
  const handleOpenImageDialog = () => {
    setOpenImageDialog(true);
  };

  /**
   * check if the contact us link is external
   */
  function isExternal() {
    return (contactUsLink ?? "").includes("https://");
  }

  /**
   * close read more drawer and go to contact us
   */
  const handleCloseDrawerAndContactUs = () => {
    setDrawerOpen(undefined);
    if (contactUsLink && !isExternal()) {
      router.push(cms.constructLink(contactUsLink), undefined, { shallow: true });
    } else if (contactUsLink && isExternal()) {
      window.open(externalLink, "_blank");
    }
  };

  /**
   * create a gallery of images for the overlay
   */
  const galleryImages = () => {
    const images: CarouselImageType[] = [];
    for (const opt of unitDetails?.unitTypeDetails ?? []) {
      if (opt.slug === navigationOption) {
        let iter = 1;
        for (const image of opt.imageCarousel?.data ?? []) {
          if (typeof image?.attributes?.url === "string") {
            images.push({
              url: LoadFromCDN.imageSizeAndQuality(image?.attributes?.url, 100, imageSize),
              alt: image?.attributes?.alternativeText ?? `${unitDetails.viewGalleryText ?? "View Gallery"} ${iter}`,
              id: `image_gallery_${iter}`,
            });
            iter++;
          }
        }
      }
    }
    return images;
  };

  /**
   * enable the user to download a floorplan for the current unit type selected
   */
  const downloadFloorplan = () => {
    const currentUnit = (unitDetails?.unitTypeDetails ?? []).filter((opt) => opt.slug === navigationOption);
    if ((currentUnit ?? []).length === 0 || !currentUnit?.[0]?.floorplan?.data?.attributes?.url) {
      toast.addMessage({
        title: "Opps, this floorplan is missing",
        subtitle: "",
        type: "error",
      });
      return;
    }
    const floorplan = LoadFromCDN.loadFromGeneralCDN(
      currentUnit?.[0]?.floorplan?.data?.attributes?.hash,
      currentUnit?.[0]?.floorplan?.data?.attributes?.ext
    );
    window.open(floorplan, `${currentUnit?.[0]?.floorplan?.data?.attributes?.hash}`);
  };

  const hasFloorplan = (): boolean => {
    const currentUnit = (unitDetails?.unitTypeDetails ?? []).filter((opt) => opt.slug === navigationOption);
    return !!((currentUnit ?? []).length === 1 && currentUnit?.[0]?.floorplan?.data?.attributes?.url);
  };

  /**
   * get the link for the legal disclaimer
   */
  function legalDisclaimerLink(legalDisclaimer: StrapiComponentType.Element.LinkOptional) {
    return legalDisclaimer?.isExternal ? legalDisclaimer?.optionalHref : cms.constructLink(legalDisclaimer?.optionalHref ?? "");
  }

  return (
    <SectionModule
      section={section}
      slug={slug}
      stylePrefix="UnitDetails"
      styleContainerId={unitDetails.id}
      snapToGrid={snapToGrid}
      classes={{
        section: classes?.section,
      }}
      searchParams={searchParams}
      quality={quality}
      lazy={lazy}
    >
      <Box
        className={cn(
          unitDetails.directionLTR ? styles.ltr : styles.rtl,
          styles.container,
          unitDetails.dynamicWidthTabs || unitDetails.tabsMinWidth ? styles.dynamicWidthTabs : undefined
        )}
        sx={{
          [`@media (min-width: ${theme.breakpointMD})`]: {
            display: unitDetails.tabsMinWidth ? "grid !important" : undefined,
            gridTemplateColumns: unitDetails.tabsMinWidth ? `1fr ${unitDetails.tabsMinWidth}px !important` : undefined,
          },
        }}
      >
        <div className={cn(styles.imgContainer, snapToGrid ? styles.snapToGrid : undefined)}>
          {(unitDetails?.unitTypeDetails ?? []).map((opt) => (
            <div key={opt.id}>
              {CMSLogic.isVideo(opt.background) ? (
                <>
                  <VideoPlayer
                    asset={opt.background}
                    videoId={`video.UnitDetails.${unitDetails.id}.${opt.id}`}
                    lazy={opt.slug === navigationOption ? "eager" : "lazy"}
                    play={opt.slug === navigationOption}
                    classes={{
                      previewPoster: cn(styles.sideImage, opt.slug === navigationOption ? styles.show : undefined, styles.video),
                      container: cn(styles.sideImage, opt.slug === navigationOption ? styles.show : undefined, styles.video),
                      video: styles.sideVideo,
                    }}
                    poster={LoadFromCDN.imageSizeAndQuality(opt.poster?.data?.attributes?.url, 90, imageSize)}
                    dontLoadUntilEager={true}
                  />
                </>
              ) : (
                <ImgBgResponsive
                  lazy={opt.slug === navigationOption ? "eager" : "lazy"}
                  imageUrl={LoadFromCDN.nextGenFormat(opt.background?.data?.attributes?.url)}
                  className={cn(styles.sideImage, opt.slug === navigationOption ? styles.show : undefined)}
                  prefetch={opt.slug === navigationOption}
                />
              )}
            </div>
          ))}
          <div className={styles.sideImageGradient} />
          <div className={styles.imgOverlayBttn}>
            {unitDetails.viewGalleryText && (
              <BaseButton
                className={styles.button}
                color="primary"
                variant="outlined"
                onClick={handleOpenImageDialog}
              >
                {unitDetails.viewGalleryText ?? "Gallery"}
              </BaseButton>
            )}
            {hasFloorplan() && (
              <BaseButton
                className={cn(styles.button)}
                color="primary"
                variant="outlined"
                onClick={downloadFloorplan}
              >
                {unitDetails.viewFloorplansText ?? "Floorplans"}
              </BaseButton>
            )}
          </div>
        </div>
        <Box
          className={styles.textContainer}
          sx={{ width: unitDetails.tabsMinWidth ? `${unitDetails.tabsMinWidth}px !important` : undefined }}
        >
          <Tabs
            orientation="horizontal"
            value={navigationOption}
            onChange={handleChange}
            aria-label="Navigation Options"
            variant="scrollable"
            scrollButtons="auto"
            visibleScrollbar={false}
            allowScrollButtonsMobile={true}
            sx={{
              ".MuiTabs-indicator": {
                left: 0,
                width: "4px",
              },
              ".MuiTabs-fixed": {
                borderBottom: `1px solid ${theme.primaryMain}33`,
              },
              [breakpointMdKey]: {
                width: "100%",
              },
              ".MuiTabs-flexContainer": {
                [breakpointMdKey]: {
                  justifyContent: "space-evenly",
                },
              },
              ".MuiTabs-flexContainer button": {
                width: "95px",
                [breakpointMdKey]: {
                  width: "unset",
                },
              },
            }}
          >
            {(unitDetails?.unitTypeDetails ?? []).map((opt, i) => (
              <Tab
                key={i}
                value={opt.slug}
                label={
                  <Typography
                    variant="body1"
                    fontWeight={opt.slug === navigationOption ? 600 : undefined}
                  >
                    {opt.tab ?? opt.title}
                  </Typography>
                }
              />
            ))}
          </Tabs>
          <div className={styles.tabPanelContainer}>
            {(unitDetails?.unitTypeDetails ?? []).map((opt, i) => (
              <div
                key={i}
                className={cn(styles.tabPanel, opt.slug === navigationOption ? styles.show : undefined)}
              >
                <UnitDetailsFacts unit={opt} />
                {isMobile && (
                  <BaseButton
                    className={styles.readMoreButton}
                    onClick={() => setDrawerOpen(opt)}
                  >
                    {unitDetails.readMoreText ?? "Read more"}
                  </BaseButton>
                )}
                <div className={styles.buttons}>
                  {unitDetails.viewGalleryText && (
                    <BaseButton
                      variant="outlined"
                      color="primary"
                      size="medium"
                      className={styles.gallery}
                      onClick={handleOpenImageDialog}
                    >
                      {unitDetails.viewGalleryText ?? "Gallery"}
                    </BaseButton>
                  )}
                  {hasFloorplan() && (
                    <BaseButton
                      variant="outlined"
                      color="primary"
                      size="medium"
                      className={styles.gallery}
                      onClick={downloadFloorplan}
                    >
                      {unitDetails.viewFloorplansText ?? "Floorplans"}
                    </BaseButton>
                  )}
                  {contactUsLink && unitDetails.contactUsButtonText && !hasProfile && (
                    <BaseButton
                      variant={isMobile ? "outlined" : "text"}
                      color="primary"
                      size="large"
                      component={Link}
                      href={isExternal() ? externalLink : cms.constructLink(`${contactUsLink}`)}
                      //@ts-ignore - props for component property {Link} are allowed
                      target={isExternal() ? "_blank" : "_self"}
                      rel={isExternal() ? "noopener noreferrer" : undefined}
                      shallow={!isExternal()}
                      className={styles.contactUsBttn}
                    >
                      {unitDetails.contactUsButtonText ?? "Contact Us"}
                    </BaseButton>
                  )}
                  {unitDetails.cta && !unitDetails.contactUsButtonText && (
                    <ButtonLinkModule
                      buttonLink={unitDetails.cta}
                      className={cn(styles.contactUsBttn, styles.cta)}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </Box>
      </Box>
      {openImageDialog && (
        <Dialog
          onClose={handleCloseImageDialog}
          open={openImageDialog}
        >
          <div className={styles.imageDialogContainer}>
            <div
              className={styles.imageDialogExit}
              onClick={handleCloseImageDialog}
            >
              <CloseIcon
                fontSize="large"
                sx={{ color: theme.secondaryMain }}
              />
            </div>
            <div className={styles.imageDialogCarousel}>
              <CarouselImage
                index={0}
                images={galleryImages() ?? []}
                autoPlay={false}
                imageClassName={styles.imageDialogImage}
                navButtonsAlwaysVisible={true}
                animation="slide"
                objectFit="contain"
                bgResponsive={true}
              />
            </div>
            <div className={styles.legalDisclaimer}>
              {section?.legalDisclaimer?.optionalHref ? (
                <Link
                  href={legalDisclaimerLink(section?.legalDisclaimer) ?? ""}
                  target={section?.legalDisclaimer?.target}
                >
                  <Typography variant="body1">{section?.legalDisclaimer?.label}</Typography>
                </Link>
              ) : (
                <Typography variant="body1">{section?.legalDisclaimer?.label}</Typography>
              )}
            </div>
          </div>
        </Dialog>
      )}
      <Drawer
        anchor="bottom"
        onClose={() => setDrawerOpen(undefined)}
        open={typeof drawerOpen !== "undefined"}
        className={styles.drawerScrollbar}
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: "100vw",
            background: theme.secondaryMain,
            zIndex: 4000,
            height: "75vh",
            borderTopLeftRadius: theme.borderRadius,
            borderTopRightRadius: theme.borderRadius,
          },
        }}
      >
        <div className={styles.drawerHeader}>
          <IconButton onClick={() => setDrawerOpen(undefined)}>
            <Image
              src={"/image/icon_exit.svg"}
              alt={"Exit Drawer"}
              width={24}
              height={24}
              priority={true}
            />
          </IconButton>
        </div>
        <div className={styles.drawerPanel}>
          {drawerOpen && (
            <UnitDetailsFacts
              unit={drawerOpen}
              showAllFacts={true}
            />
          )}
          {contactUsLink && unitDetails.contactUsButtonText && !hasProfile && (
            <BaseButton
              variant={isMobile ? "outlined" : "text"}
              color="primary"
              size="large"
              className={styles.contactUsBttn}
              onClick={handleCloseDrawerAndContactUs}
            >
              {unitDetails.contactUsButtonText ?? "Contact Us"}
            </BaseButton>
          )}
        </div>
      </Drawer>
    </SectionModule>
  );
}

export default UnitDetails;
